import type { JsonSchemaType } from '@gmn-portal/backend';
import { Archive, Edit3, Info, Ship, Users } from 'lucide-react';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  useCustomer,
  useObject,
  useObjectCategories,
  useObjectCategory,
  useSystemShipDetail,
  useUpdateSystemShip,
} from '@/core/api';
import { PayloadApiError } from '@/core/api/client';
import { useAuth } from '@/core/auth';
import { useIntl } from '@/core/i18n/use-intl';
import { DetailPageLayout, TabNav } from '@/shared/components/layout';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Card,
  CardContent,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui';
import { getCustomerDetailHref, getObjectDetailHref, scrollToTarget } from '@/shared/utils';
import { getFieldPermission } from '@/shared/utils/metadata';
import {
  canReadService,
  resolveUserGroup,
  resolveUserGroupDomain,
} from '@/shared/utils/permissions';
import { ObjectMetadataSidemenu } from '../components/object-metadata-sidemenu';
import { useScrollSpy } from '../components/use-scroll-spy';
import { SystemShipCurrentAssignmentPanel } from './system-ship-current-assignment-panel';

const formatDay = (value?: string | null) =>
  value ? DateTime.fromISO(value).toFormat('dd.MM.yyyy') : '-';

type SystemShipFormValues = {
  name: string;
  imo: string;
  flag: string;
  callSign: string;
  mmsi: string;
};

type SystemShipFormErrors = Partial<Record<keyof SystemShipFormValues, string>>;

const EMPTY_FORM_VALUES: SystemShipFormValues = {
  name: '',
  imo: '',
  flag: '',
  callSign: '',
  mmsi: '',
};

const HIDDEN_SHIP_METADATA_FIELDS = new Set(['imo', 'flag', 'callSign', 'mmsi']);

const getOrderedFieldNames = (metadataSchema: JsonSchemaType) => {
  const fromOrder = metadataSchema.propertyOrder?.length ? metadataSchema.propertyOrder : [];
  return fromOrder.length > 0 ? fromOrder : Object.keys(metadataSchema.properties || {});
};

const getKnownGroupIds = (metadataSchema: JsonSchemaType) =>
  new Set((metadataSchema.groups ?? []).map((group) => group.id));

const getRelationshipId = (value: unknown): string | null => {
  if (!value) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && 'id' in value && typeof value.id === 'string') {
    return value.id;
  }
  return null;
};

export function SystemShipDetails() {
  const { id = '' } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { t, locale } = useIntl();
  const { user } = useAuth();
  const updateSystemShip = useUpdateSystemShip();
  const domain = resolveUserGroupDomain(user);
  const group = resolveUserGroup(user);
  const canReadObjectDetails = canReadService(group, 'objects');
  const canReadCustomers = canReadService(group, 'customers');
  const canEditSystemShip = group?.key === 'gmn-admin';
  const { data, isLoading } = useSystemShipDetail(id, { disabled: !id });
  const activeObjectId = data?.activeObject?.id ?? '';
  const { data: activeObject } = useObject(activeObjectId, {
    disabled: !activeObjectId,
  });
  const activeCategoryId =
    activeObject?.category && typeof activeObject.category === 'object'
      ? activeObject.category.id
      : typeof activeObject?.category === 'string'
        ? activeObject.category
        : '';
  const { data: activeObjectCategory } = useObjectCategory(activeCategoryId, {
    disabled: !activeCategoryId,
  });
  const [isEditMode, setIsEditMode] = useState(false);
  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);
  const [formValues, setFormValues] = useState<SystemShipFormValues>(EMPTY_FORM_VALUES);
  const [formErrors, setFormErrors] = useState<SystemShipFormErrors>({});
  const [scrollRoot, setScrollRoot] = useState<HTMLElement | null>(null);
  const { data: shipCategories } = useObjectCategories({
    where: {
      or: [
        { name: { equals: 'Schiff' } },
        { name: { equals: 'Jack-Up-Schiff' } },
        { name: { equals: 'Jack Up Schiff' } },
        { name: { equals: 'Jack-Up' } },
      ],
    },
    limit: 1,
  });

  useEffect(() => {
    if (typeof document === 'undefined') return;
    setScrollRoot(document.querySelector('main.overflow-y-auto') as HTMLElement | null);
  }, []);

  useEffect(() => {
    if (!data?.systemShip) return;

    setFormValues({
      name: data.systemShip.name || '',
      imo: data.systemShip.imo || '',
      flag: data.systemShip.flag || '',
      callSign: data.systemShip.callSign || '',
      mmsi: data.systemShip.mmsi || '',
    });
    setFormErrors({});
  }, [data?.systemShip]);

  const groupedHistory = useMemo(() => {
    const map = new Map<string, NonNullable<typeof data>['historicalObjects']>();

    for (const item of data?.historicalObjects ?? []) {
      const customer =
        typeof item.assignedCustomer === 'string'
          ? { id: item.assignedCustomer, name: item.assignedCustomer }
          : {
              id: item.assignedCustomer?.id ?? 'unknown',
              name: item.assignedCustomer?.name ?? 'Unknown customer',
            };

      const key = customer.id ?? 'unknown';
      const existing = map.get(key) ?? [];
      existing.push(item);
      map.set(key, existing);
    }

    return Array.from(map.entries());
  }, [data?.historicalObjects]);

  const pageTitle = data?.systemShip?.name || t('objects.object');
  const currentCustomerId = getRelationshipId(data?.activeObject?.assignedCustomer);
  const shouldResolveCurrentCustomerById =
    Boolean(currentCustomerId) && typeof data?.activeObject?.assignedCustomer !== 'object';
  const { data: currentCustomerById } = useCustomer(
    shouldResolveCurrentCustomerById ? (currentCustomerId ?? '') : ''
  );
  const currentCustomer =
    data?.activeObject?.assignedCustomer && typeof data.activeObject.assignedCustomer === 'object'
      ? data.activeObject.assignedCustomer
      : currentCustomerById;
  const currentCustomerHref = getCustomerDetailHref({
    customerId: currentCustomerId ?? currentCustomer?.id,
    domain,
    canReadCustomers,
  });

  const backCategoryId = shipCategories?.docs?.[0]?.id;
  const backHref = `${domain === 'customer' ? '/c/objects' : '/gmn/objects'}${
    backCategoryId ? `?category=${backCategoryId}` : ''
  }`;
  const currentTab = location.pathname.endsWith('/history') ? 'history' : 'details';
  const tabBasePath = `${domain === 'customer' ? '/c' : '/gmn'}/objects/ships/${id}`;
  const isSystemShipArchived = Boolean(data?.systemShip?.archived);
  const archiveLabel = isSystemShipArchived
    ? t('objects.ships.unarchiveShip')
    : t('objects.ships.archiveShip');
  const activeCategoryMetadataSchema = activeObjectCategory?.metadataSchema as
    | JsonSchemaType
    | undefined;

  const activeAssignmentMetadataSections = useMemo(() => {
    if (!activeCategoryMetadataSchema?.properties) {
      return [];
    }

    const visibleFieldNames = (fieldNames: string[]) =>
      fieldNames.filter((fieldName) => {
        const field = activeCategoryMetadataSchema.properties?.[fieldName];
        if (!field || !getFieldPermission(field, group?.key ?? null).read) {
          return false;
        }
        return !HIDDEN_SHIP_METADATA_FIELDS.has(fieldName);
      });

    const orderedFieldNames = getOrderedFieldNames(activeCategoryMetadataSchema);
    const groups = [...(activeCategoryMetadataSchema.groups ?? [])].sort((left, right) => {
      return left.order - right.order;
    });
    const knownGroupIds = getKnownGroupIds(activeCategoryMetadataSchema);

    const sections = groups
      .map((metadataGroup) => ({
        id: `system-ship-assignment-group-${metadataGroup.id}`,
        label:
          locale === 'en-US' && metadataGroup.labelEn
            ? metadataGroup.labelEn
            : metadataGroup.labelDe,
        fieldNames: visibleFieldNames(
          orderedFieldNames.filter(
            (fieldName) =>
              activeCategoryMetadataSchema.properties?.[fieldName]?.groupId === metadataGroup.id
          )
        ),
      }))
      .filter((section) => section.fieldNames.length > 0);

    const ungroupedFieldNames = visibleFieldNames(
      orderedFieldNames.filter((fieldName) => {
        const field = activeCategoryMetadataSchema.properties?.[fieldName];
        if (!field) return false;
        if (!field.groupId) return true;
        return !knownGroupIds.has(field.groupId);
      })
    );

    if (ungroupedFieldNames.length > 0) {
      sections.push({
        id: 'system-ship-assignment-group-ungrouped',
        label: t('metadata.builder.ungrouped'),
        fieldNames: ungroupedFieldNames,
      });
    }

    return sections;
  }, [activeCategoryMetadataSchema, group?.key, locale, t]);

  const handleChange = (key: keyof SystemShipFormValues, value: string) => {
    setFormValues((current) => ({
      ...current,
      [key]: value,
    }));
    setFormErrors((current) => {
      if (!current[key]) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });
  };

  const handleCancelEdit = () => {
    if (data?.systemShip) {
      setFormValues({
        name: data.systemShip.name || '',
        imo: data.systemShip.imo || '',
        flag: data.systemShip.flag || '',
        callSign: data.systemShip.callSign || '',
        mmsi: data.systemShip.mmsi || '',
      });
    }
    setFormErrors({});
    setIsEditMode(false);
  };

  const handleSave = async () => {
    if (!id) return;

    if (!formValues.imo.trim()) {
      const message = t('objects.ships.imoRequired');
      setFormErrors({ imo: message });
      toast.error(message);
      return;
    }

    try {
      await updateSystemShip.mutateAsync({
        id,
        data: {
          name: formValues.name.trim(),
          imo: formValues.imo.trim(),
          flag: formValues.flag.trim() || null,
          callSign: formValues.callSign.trim() || null,
          mmsi: formValues.mmsi.trim() || null,
        },
      });
      toast.success(t('objects.objectUpdatedSuccessfully'));
      setFormErrors({});
      setIsEditMode(false);
    } catch (error) {
      if (error instanceof PayloadApiError) {
        const firstError =
          Array.isArray(error.response?.errors) && error.response.errors.length > 0
            ? (error.response.errors[0] as { message?: string })
            : null;

        if (firstError?.message === 'ShipImoAlreadyExists') {
          const message = t('objects.ships.imoDuplicate');
          setFormErrors({ imo: message });
          toast.error(message);
          return;
        }

        if (firstError?.message === 'ShipImoRequired') {
          const message = t('objects.ships.imoRequired');
          setFormErrors({ imo: message });
          toast.error(message);
          return;
        }
      }

      toast.error(t('objects.errorSavingObject'));
    }
  };

  const handleToggleArchive = async () => {
    if (!id) return;

    try {
      await updateSystemShip.mutateAsync({
        id,
        data: {
          archived: !isSystemShipArchived,
        },
      });
      toast.success(
        isSystemShipArchived
          ? t('objects.ships.shipUnarchivedSuccessfully')
          : t('objects.ships.shipArchivedSuccessfully')
      );
      setIsArchiveDialogOpen(false);
      if (!isSystemShipArchived) {
        navigate(backHref);
      }
    } catch {
      toast.error(
        isSystemShipArchived
          ? t('objects.ships.errorUnarchivingShip')
          : t('objects.ships.errorArchivingShip')
      );
    }
  };

  const sections = useMemo(
    () => [
      {
        id: 'system-ship-master-data',
        label: t('objects.masterData'),
        kind: 'master-data' as const,
      },
      {
        id: 'system-ship-current-assignment',
        label: t('objects.ships.currentAssignment'),
        kind: 'category-group' as const,
      },
      ...(data?.activeObject
        ? [
            {
              id: 'system-ship-assignment-periods',
              label: t('objects.ships.activePeriodsTitle'),
              kind: 'category-group' as const,
            },
            ...activeAssignmentMetadataSections.map((section) => ({
              id: section.id,
              label: section.label,
              kind: 'category-group' as const,
            })),
          ]
        : []),
    ],
    [activeAssignmentMetadataSections, data?.activeObject, t]
  );

  const activeSectionId = useScrollSpy(
    sections.map((section) => section.id),
    {
      root: scrollRoot,
      rootMargin: '-15% 0px -75% 0px',
    }
  );

  const scrollToSection = (sectionId: string) => {
    scrollToTarget(sectionId, { root: scrollRoot });
  };

  const metaItems = data
    ? [
        {
          icon: Info,
          label: data.activeObject ? t('objects.ships.activeToday') : t('objects.ships.inactive'),
        },
        ...(domain === 'customer'
          ? []
          : [
              {
                icon: Users,
                label: currentCustomer?.name || t('objects.ships.noCurrentAssignment'),
                href: currentCustomerHref ?? undefined,
              },
            ]),
      ]
    : undefined;

  const renderSectionHeading = (title: string) => (
    <div className="flex items-center gap-3 mb-4">
      <h4 className="text-sm font-semibold text-foreground">{title}</h4>
      <div className="h-px flex-1 bg-border" />
    </div>
  );

  const renderMasterDataSection = () => (
    <div className="space-y-4">
      {renderSectionHeading(t('objects.masterData'))}
      <div className="grid grid-cols-6 gap-4">
        {[
          { key: 'name', label: t('common.name') },
          { key: 'imo', label: t('objects.ships.imoLabel'), required: true },
          { key: 'flag', label: t('objects.ships.flagLabel') },
          { key: 'callSign', label: t('objects.ships.callSignLabel') },
          { key: 'mmsi', label: t('objects.ships.mmsiLabel') },
        ].map((field) => (
          <div key={field.key} className="col-span-6 md:col-span-3 lg:col-span-2">
            <div className="rounded-xl border border-border bg-card p-4">
              <Label className="mb-1 block text-xs font-medium text-muted-foreground">
                {field.label}
                {isEditMode && field.required ? (
                  <span className="ml-1 text-destructive">*</span>
                ) : null}
              </Label>
              {isEditMode ? (
                <div className="space-y-2">
                  <Input
                    aria-label={field.label}
                    aria-required={field.required}
                    aria-invalid={Boolean(formErrors[field.key as keyof SystemShipFormValues])}
                    value={formValues[field.key as keyof SystemShipFormValues]}
                    onChange={(event) =>
                      handleChange(field.key as keyof SystemShipFormValues, event.target.value)
                    }
                  />
                  {formErrors[field.key as keyof SystemShipFormValues] ? (
                    <p className="text-sm font-medium text-destructive">
                      {formErrors[field.key as keyof SystemShipFormValues]}
                    </p>
                  ) : null}
                </div>
              ) : (
                <div className="text-sm font-medium">
                  {formValues[field.key as keyof SystemShipFormValues] || '-'}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderHistorySection = () => (
    <div className="space-y-4">
      {renderSectionHeading(t('objects.ships.historyTitle'))}
      <Card>
        <CardContent className="pt-5">
          {groupedHistory.length === 0 ? (
            <div className="text-sm text-muted-foreground">{t('objects.ships.historyEmpty')}</div>
          ) : (
            <div className="space-y-6">
              {groupedHistory.map(([customerId, items]) => {
                const customerName =
                  (typeof items[0]?.assignedCustomer === 'object'
                    ? items[0]?.assignedCustomer?.name
                    : items[0]?.assignedCustomer) || 'Unknown customer';

                return (
                  <div key={customerId} className="space-y-3">
                    {domain !== 'customer' ? (
                      <div className="text-sm font-semibold text-foreground">{customerName}</div>
                    ) : null}
                    <div className="space-y-3">
                      {items.map((item) => {
                        const href = getObjectDetailHref({
                          objectId: item.id,
                          domain,
                          canReadObjects: canReadObjectDetails,
                        });
                        const assignmentHref = href ? `${href}?assignment=1` : null;

                        return (
                          <div
                            key={item.id}
                            className="rounded-xl border border-border bg-card p-4"
                          >
                            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                              <div className="space-y-1">
                                <div className="text-sm font-medium">
                                  {item.name || data?.systemShip.name}
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  {(item.activePeriods ?? [])
                                    .map(
                                      (period) =>
                                        `${formatDay(period.startDate)} - ${formatDay(period.endDate)}`
                                    )
                                    .join(', ') || '-'}
                                </div>
                              </div>
                              {assignmentHref ? (
                                <Button asChild variant="outline" size="sm">
                                  <Link to={assignmentHref}>
                                    {t('objects.ships.openAssignment')}
                                  </Link>
                                </Button>
                              ) : null}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  return (
    <DetailPageLayout
      containerProps={{ animation: 'slide-right' }}
      headerProps={{
        backHref,
        backLabel: t('objects.backToOverview'),
        title: pageTitle,
        icon: Ship,
        metaItems,
        subtitle: t('objects.ships.assignmentCardDescription'),
        actions:
          canEditSystemShip && !isLoading && data && !isEditMode && currentTab === 'details' ? (
            <div className="flex items-center justify-end gap-2">
              <AlertDialog open={isArchiveDialogOpen} onOpenChange={setIsArchiveDialogOpen}>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsArchiveDialogOpen(true)}
                  disabled={updateSystemShip.isPending}
                >
                  <Archive size={16} className="mr-2" />
                  {archiveLabel}
                </Button>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{archiveLabel}</AlertDialogTitle>
                    <AlertDialogDescription>
                      {t(
                        isSystemShipArchived
                          ? 'objects.ships.unarchiveShipConfirmation'
                          : 'objects.ships.archiveShipConfirmation',
                        {
                          name: data.systemShip.name,
                        }
                      )}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleToggleArchive}
                      disabled={updateSystemShip.isPending}
                    >
                      {updateSystemShip.isPending ? t('common.updating') : archiveLabel}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              {!isSystemShipArchived ? (
                <Button type="button" onClick={() => setIsEditMode(true)}>
                  <Edit3 size={16} className="mr-2" />
                  {t('common.edit')}
                </Button>
              ) : null}
            </div>
          ) : undefined,
        secondaryAction:
          canEditSystemShip && !isLoading && data && isEditMode && currentTab === 'details'
            ? {
                label: t('common.discard'),
                onClick: handleCancelEdit,
              }
            : undefined,
        primaryAction:
          canEditSystemShip && !isLoading && data && isEditMode && currentTab === 'details'
            ? {
                label: t('common.update'),
                onClick: () => void handleSave(),
                disabled: updateSystemShip.isPending,
                loading: updateSystemShip.isPending,
              }
            : undefined,
      }}
    >
      {isLoading || !data ? (
        <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
      ) : (
        <>
          <TabNav
            tabs={[
              { value: 'details', label: t('objects.masterData') },
              { value: 'history', label: t('objects.ships.historyTitle') },
            ]}
            basePath={tabBasePath}
            variant="underline"
          />

          {currentTab === 'history' ? (
            <div className="grid gap-6">
              <div id="system-ship-history" className="scroll-mt-28">
                {renderHistorySection()}
              </div>
            </div>
          ) : (
            <div className="grid gap-6 lg:grid-cols-12">
              <div className="lg:col-span-3">
                <div className="lg:hidden">
                  <Select
                    value={activeSectionId ?? sections[0]?.id ?? 'system-ship-master-data'}
                    onValueChange={(sectionId) => scrollToSection(sectionId)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {sections.map((section) => (
                        <SelectItem key={section.id} value={section.id}>
                          {section.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="hidden lg:block sticky top-4 self-start max-h-[calc(100vh-2rem)] overflow-auto">
                  <ObjectMetadataSidemenu
                    items={sections}
                    activeId={activeSectionId}
                    onSelect={scrollToSection}
                  />
                </div>
              </div>

              <div className="lg:col-span-9 grid gap-6">
                <div id="system-ship-master-data" className="scroll-mt-28">
                  {renderMasterDataSection()}
                </div>

                <div id="system-ship-current-assignment" className="scroll-mt-28">
                  {data.activeObject ? (
                    <SystemShipCurrentAssignmentPanel
                      activeObjectId={data.activeObject.id}
                      occupiedPeriods={data.occupiedPeriods}
                      systemShipId={id}
                      sectionIds={{
                        assignment: 'system-ship-current-assignment',
                        periods: 'system-ship-assignment-periods',
                      }}
                      metadataSections={activeAssignmentMetadataSections}
                    />
                  ) : (
                    <div className="space-y-4">
                      {renderSectionHeading(t('objects.ships.currentAssignment'))}
                      <Card>
                        <CardContent className="pt-5">
                          <div className="flex items-start gap-3">
                            <Info className="mt-0.5 size-4 text-muted-foreground" />
                            <div>
                              <div className="text-sm font-semibold">
                                {t('objects.ships.noCurrentAssignment')}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {t('objects.ships.noCurrentAssignmentDescription')}
                              </div>
                              {data.occupiedPeriods.length > 0 ? (
                                <div className="mt-4 space-y-2">
                                  <div className="text-xs font-medium text-muted-foreground">
                                    {t('objects.ships.occupiedPeriods')}
                                  </div>
                                  <div className="flex flex-wrap gap-2">
                                    {data.occupiedPeriods.map((period, index) => (
                                      <span
                                        key={`${period.startDate}-${period.endDate}-${index}`}
                                        className="rounded-full bg-secondary px-3 py-1 text-xs font-medium"
                                      >
                                        {formatDay(period.startDate)} - {formatDay(period.endDate)}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </DetailPageLayout>
  );
}
